import { QueryClient } from '@tanstack/react-query';

import { ApiError } from './http.ts';

/**
 * Server state is pushed to us over SignalR, so aggressive background refetching is
 * mostly wasted battery on a phone in a shop. We keep data fresh via the hub and only
 * refetch on reconnect, which doubles as the offline reconciliation trigger.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * Fire the request even when the browser says there is no network.
       *
       * React Query's default is `online`, which *pauses* a query while
       * `navigator.onLine` is false: the fetch is never issued, the status stays
       * `pending` for ever, and every "Loading…" in this app is a status check. That is
       * the right default for an app whose data lives only on a server, and exactly wrong
       * for this one — the service worker is holding the last known list, and a request
       * that is never made can never be answered from a cache. Reopening the app with no
       * signal sat on "Loading…" indefinitely for precisely this reason.
       *
       * `offlineFirst` issues the request once, which the service worker answers from
       * `list-data` / `group-data`, and only then pauses retries until the connection
       * comes back. It is also what makes mutations reach the offline queue at all: the
       * queue is built from `onError`, and a paused mutation never calls its function, so
       * a tap in a dead spot was applied optimistically and then quietly forgotten when
       * the tab was killed.
       */
      networkMode: 'offlineFirst',
      staleTime: 30_000,
      gcTime: 24 * 60 * 60 * 1000, // survive a long walk round the shop
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        // No point retrying a 4xx; the request is wrong, not unlucky.
        if (error instanceof ApiError && error.isClientError) {
          return false;
        }
        return failureCount < 3;
      },
    },
    mutations: {
      // See above: a mutation must be allowed to fail so that `onError` can put the
      // user's intent into the queue. Pausing it here would hold it in memory, unordered
      // and unpersisted, which is the job src/offline already does properly.
      networkMode: 'offlineFirst',
      // Mutations are retried by the offline queue, not by React Query, so that
      // ordering is preserved across a reconnect. See src/offline.
      retry: false,
    },
  },
});
